import { $Keys } from 'utility-types';

import { createContext, useContext, useState } from 'react';
import { OMQHelpSupport } from '@admin/shared/components/support/omq-help';
import { UserlikeSupport } from '@admin/shared/components/support/userlike';

/**
 * Type of context
 */
type SupportContextType = {
  supportData: SupportData | null;
  showSupport: () => void;
  hideSupport: () => void;
};

/**
 * Type of component properties.
 */
type SupportWrapperProps = {
  children: JSX.Element | JSX.Element[];
};

// Support type options
const SupportTypes = {
  OMQ_HELP: 'OMQ_HELP',
  USERLIKE: 'USERLIKE',
};

// type for support options
type SupportType = $Keys<typeof SupportTypes>;

// type of support data
export type SupportData = {
  account: string;
  apiKey: string;
  type: SupportType;
};

// create (react) context
const SupportContext = createContext<SupportContextType>({} as any);

// provide hook to access context
export const useSupportContext = (): SupportContextType => {
  const context = useContext(SupportContext);
  if (context == null) {
    throw new Error(
      'SupportContext is null - Did you forget to wrap your component with SupportWrapper?',
    );
  }

  return context;
};

/**
 * Render support component depending on given type.
 * @param {SupportType} type - type of support
 *
 * @returns {Node|null}
 */
function renderSupportComponent(type: SupportType) {
  switch (type) {
    case SupportTypes.OMQ_HELP:
      return <OMQHelpSupport />;

    case SupportTypes.USERLIKE:
      return <UserlikeSupport />;

    default:
      return null;
  }
}

/**
 * Wrapper for Support context
 * *
 * @param children
 *
 * @returns {JSX.Element}
 */
export const SupportWrapper = ({ children }: SupportWrapperProps): JSX.Element => {
  const [isSupportActive, setIsSupportActive] = useState<boolean>(false);
  const [supportData] = useState<SupportData | null>(window.defaultData.support || null);

  /**
   * Show support component
   */
  const showSupport = () => {
    if (supportData == null) {
      return;
    }

    setIsSupportActive(true);
  };

  /**
   * Hide support component
   */
  const hideSupport = () => {
    setIsSupportActive(false);
  };

  const contextValue = {
    supportData,
    showSupport,
    hideSupport,
  };

  // render support component
  // if available & active
  const supportContent =
    isSupportActive && supportData != null ? renderSupportComponent(supportData.type) : null;

  // render context provider
  return (
    <SupportContext.Provider value={contextValue}>
      {children}
      {supportContent}
    </SupportContext.Provider>
  );
};
